import { v4 as uuidv4 } from 'uuid'
import { storage } from '~config/firebase'
import {
    ref,
    uploadString,
    getDownloadURL,
    FullMetadata,
    getMetadata,
    UploadMetadata,
} from 'firebase/storage'

const UUID_FILENAME_PREFIX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i

function getDataUrlContentType(dataUrl: string): string | undefined {
    const match = /^data:([^;]+);/i.exec(dataUrl)
    return match?.[1]
}

/**
 * Callers sometimes pass `${uuid}-${originalName}` for uniqueness. The helper
 * already generates its own UUID, so strip a leading one to avoid nesting.
 */
function stripUuidFilenamePrefix(filename: string): string {
    return filename.replace(UUID_FILENAME_PREFIX, '')
}

function getFileExtension(filename: string): string {
    const basename = filename.split(/[/\\]/).pop() ?? filename
    const lastDot = basename.lastIndexOf('.')
    if (lastDot <= 0 || lastDot === basename.length - 1) return ''

    const ext = basename.slice(lastDot + 1)
    if (!/^[a-zA-Z0-9]{1,10}$/.test(ext)) return ''

    return `.${ext.toLowerCase()}`
}

function getExtensionFromContentType(
    contentType: string | undefined,
): string {
    switch (contentType?.split(';')[0]?.trim().toLowerCase()) {
        case 'image/jpeg':
        case 'image/jpg':
            return '.jpeg'
        case 'image/png':
            return '.png'
        case 'image/gif':
            return '.gif'
        case 'image/webp':
            return '.webp'
        case 'application/pdf':
            return '.pdf'
        case 'video/mp4':
            return '.mp4'
        case 'video/quicktime':
            return '.mov'
        default:
            return ''
    }
}

function sanitizeObjectFilename(filename: string): string {
    return filename
        .replace(/[/\\?#%]/g, '_')
        .split('')
        .filter(char => {
            const code = char.charCodeAt(0)
            return code > 31 && code !== 127
        })
        .join('')
        .trim()
}

function getDisplayFilename(
    name: string,
    contentType: string | undefined,
): string {
    const stripped = stripUuidFilenamePrefix(name).trim()
    if (stripped) return stripped

    const extension = getExtensionFromContentType(contentType)
    return extension ? `attachment${extension}` : 'attachment'
}

/**
 * Unique Storage object name that still ends with the real extension so
 * browsers (and ezyVet) treat right-click Save As as JPEG/PNG/PDF.
 */
function buildUniqueObjectName(
    displayName: string,
    contentType: string | undefined,
): string {
    const sanitized = sanitizeObjectFilename(displayName)
    const extension =
        getFileExtension(sanitized) || getExtensionFromContentType(contentType)

    if (getFileExtension(sanitized)) {
        return `${uuidv4()}-${sanitized}`
    }

    const base = sanitized.replace(/\.+$/, '') || 'attachment'
    return `${uuidv4()}-${base}${extension}`
}

function encodeRFC5987Filename(filename: string): string {
    return encodeURIComponent(filename).replace(/['()*]/g, char => {
        return `%${char.charCodeAt(0).toString(16).toUpperCase()}`
    })
}

function buildInlineContentDisposition(displayName: string): string {
    const asciiFallback = displayName
        .replace(/[^\x20-\x7E]/g, '_')
        .replace(/["\\]/g, '_')
    return `inline; filename="${asciiFallback}"; filename*=utf-8''${encodeRFC5987Filename(displayName)}`
}

/**
 * Upload a chat (or similar) attachment from a data URL.
 *
 * Object keys stay UUID-prefixed so same display names cannot overwrite each
 * other, but the original extension (and Content-Disposition filename) are
 * preserved so "Save image as…" keeps `.jpeg` / `.png`.
 */
export async function uploadMessageAttachment(
    path: string,
    name: string,
    url: string,
): Promise<{ downloadUrl: string; metadata: FullMetadata } | undefined> {
    try {
        const contentType = getDataUrlContentType(url)
        const displayName = getDisplayFilename(name, contentType)
        const objectPath = `${path}/${buildUniqueObjectName(displayName, contentType)}`
        const storageRef = ref(storage, objectPath)

        const metadata: UploadMetadata = {
            contentDisposition: buildInlineContentDisposition(displayName),
        }
        if (contentType) {
            metadata.contentType = contentType
        }

        await uploadString(storageRef, url, 'data_url', metadata)

        const downloadUrl = await getDownloadURL(storageRef)
        const fullMetadata = await getMetadata(storageRef)

        return {
            downloadUrl,
            metadata: fullMetadata,
        }
    } catch (e) {
        console.error(
            `(uploadMessageAttachment) Failed to upload "${name}" to ${path}`,
            e,
        )
    }
    return
}

export function getStoragePathFromUrl(url: string) {
    const splitUrl = url.split('/o/')
    return splitUrl[1]
        .split('?')[0]
        .replaceAll('%2F', '/')
        .replaceAll('%3A', ':')
}
